/**
 * @file chefStats.service.ts
 * @description Dedicated service for querying chef production KPIs and top dishes.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Executes the secure backend RPC `get_chef_production_stats`.
 * 2. Formats production speed, delays, rush orders, and leaderboard data.
 */

import { supabase } from '../../../config/supabase.config';
import type { ChefProductionStats, StatsTimeframe } from '../../../types/stats.types';

// In-memory cache for chef production stats (60s TTL) to prevent repeated RPC executions
const chefStatsCache = new Map<string, { data: ChefProductionStats; timestamp: number }>();
const CHEF_STATS_CACHE_TTL_MS = 60 * 1000;

export const fetchChefProductionStats = async (
  venueId: string,
  timeframe: StatsTimeframe = 'day'
): Promise<ChefProductionStats> => {
  const cacheKey = `${venueId}:${timeframe}`;
  const cached = chefStatsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CHEF_STATS_CACHE_TTL_MS) {
    return cached.data;
  }

  const { data, error } = await supabase.rpc('get_chef_production_stats', {
    p_venue_id: venueId,
    p_timeframe: timeframe,
  });

  if (error) {
    console.error('[chefStats.service] Failed to fetch production stats:', error);
    throw error;
  }

  const formatted: ChefProductionStats = {
    timeframe,
    timezone: data?.timezone || undefined,
    orders_prepared: Number(data?.orders_prepared) || 0,
    items_prepared: Number(data?.items_prepared) || 0,
    avg_prep_minutes: Number(data?.avg_prep_minutes) || 0,
    delayed_orders: Number(data?.delayed_orders) || 0,
    rush_orders: Number(data?.rush_orders) || 0,
    items_value: data?.items_value !== null ? Number(data?.items_value) : null,
    top_dishes: Array.isArray(data?.top_dishes) ? data.top_dishes : [],
    recent_orders: Array.isArray(data?.recent_orders) ? data.recent_orders : [],
    prior_years_totals: Array.isArray(data?.prior_years_totals) ? data.prior_years_totals : [],
    permissions: data?.permissions || {
      show_production_counts: true,
      show_prep_times: true,
      show_delayed_counts: true,
      show_rush_counts: true,
      show_top_dishes: true,
      show_items_value: false,
    },
  };

  chefStatsCache.set(cacheKey, { data: formatted, timestamp: Date.now() });
  return formatted;
};
